import { KeyRound } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { changeMemberPassword } from '../api/memberAuth';
import { PageSeo } from '../components/PageSeo';
import { PasswordField } from '../components/PasswordField';
import { SubpageLayout } from '../components/SubpageLayout';
import { useAuth } from '../context/AuthContext';

export function ChangePasswordPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { authToken, authMode, logout } = useAuth();

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const isSimulation = authMode === 'simulation';

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');

        if (newPassword !== confirmPassword) {
            setError(t('changePasswordPage.passwordMismatch'));
            return;
        }

        if (!authToken) {
            setError(t('changePasswordPage.sessionRequired'));
            return;
        }

        setSubmitting(true);
        try {
            await changeMemberPassword(authToken, {
                currentPassword,
                newPassword,
                confirmPassword,
            });
            toast.warning(t('changePasswordPage.toastSuccessLogout'), {
                autoClose: 4500,
            });
            logout();
            window.setTimeout(() => {
                navigate('/login', { replace: true });
            }, 600);
        } catch (err) {
            const msg =
                err && typeof err === 'object' && 'message' in err
                    ? String(/** @type {{ message?: unknown }} */ (err).message)
                    : '';
            setError(msg || t('changePasswordPage.errorFallback'));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <>
            <PageSeo
                title={`${t('changePasswordPage.title')} — ${t('header.rotaryAlt')}`}
                description={t('seo.changePassword')}
                noindex
            />
            <SubpageLayout
                title={t('changePasswordPage.title')}
                subtitle={t('changePasswordPage.subtitle')}
                contentClassName="login-page"
            >
                <div className="login-wrap">
                    <div className="login-card">
                        <div className="login-card-icon-wrap" aria-hidden>
                            <KeyRound
                                size={22}
                                strokeWidth={1.85}
                                className="login-card-icon"
                            />
                        </div>
                        {isSimulation ? (
                            <p className="login-demo-note mb-4">
                                <strong>
                                    {t('changePasswordPage.simulationLabel')}
                                </strong>{' '}
                                {t('changePasswordPage.simulationBody')}
                            </p>
                        ) : null}
                        <form onSubmit={handleSubmit} noValidate>
                            <PasswordField
                                id="change-password-current"
                                name="currentPassword"
                                label={t(
                                    'changePasswordPage.currentPasswordLabel',
                                )}
                                value={currentPassword}
                                onChange={(e) =>
                                    setCurrentPassword(e.target.value)
                                }
                                autoComplete="current-password"
                                disabled={isSimulation || submitting}
                                className="mb-3"
                            />
                            <PasswordField
                                id="change-password-new"
                                name="newPassword"
                                label={t('changePasswordPage.newPasswordLabel')}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                autoComplete="new-password"
                                disabled={isSimulation || submitting}
                                className="mb-3"
                            />
                            <PasswordField
                                id="change-password-confirm"
                                name="confirmPassword"
                                label={t(
                                    'changePasswordPage.confirmPasswordLabel',
                                )}
                                value={confirmPassword}
                                onChange={(e) =>
                                    setConfirmPassword(e.target.value)
                                }
                                autoComplete="new-password"
                                disabled={isSimulation || submitting}
                            />
                            {error ? (
                                <div
                                    className="login-alert login-alert-error"
                                    role="alert"
                                >
                                    {error}
                                </div>
                            ) : null}
                            <button
                                type="submit"
                                className="btn login-submit-btn w-100"
                                disabled={isSimulation || submitting}
                            >
                                {submitting
                                    ? t('changePasswordPage.submitLoading')
                                    : t('changePasswordPage.submit')}
                            </button>
                        </form>
                        <p className="login-back mt-4 mb-0 text-center">
                            <Link to="/" className="login-back-link">
                                {t('changePasswordPage.backHome')}
                            </Link>
                        </p>
                    </div>
                </div>
            </SubpageLayout>
        </>
    );
}
